"use client";

import React, { useRef, useState } from "react";
import { X, MessageCircle } from "lucide-react";
import { cn } from "@gettactic/helpers";
import { Button } from "@cmp-src/primitives/button";

export type ChatPosition = "bottom-right" | "bottom-left";
export type ChatSize = "sm" | "md" | "lg" | "xl" | "full";

const chatConfig = {
  dimensions: {
    sm: "md:max-w-sm md:max-h-[500px]",
    md: "md:max-w-md md:max-h-[600px]",
    lg: "md:max-w-lg md:max-h-[700px]",
    xl: "md:max-w-xl md:max-h-[800px]",
    full: "md:w-full md:h-full"
  },
  positions: {
    "bottom-right": "bottom-5 right-5",
    "bottom-left": "bottom-5 left-5"
  },
  chatPositions: {
    "bottom-right": "md:bottom-[calc(100%+10px)] md:right-0",
    "bottom-left": "md:bottom-[calc(100%+10px)] md:left-0"
  },
  states: {
    open: "pointer-events-auto opacity-100 visible scale-100 translate-y-0",
    closed: "pointer-events-none opacity-0 invisible scale-100 sm:translate-y-5"
  }
};

interface ExpandableChatProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: ChatPosition;
  size?: ChatSize;
  icon?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  toggleOnly?: boolean;
}

const ExpandableChat: React.FC<ExpandableChatProps> = ({
  className,
  position = "bottom-left",
  size = "md",
  icon,
  children,
  isOpen: controlledIsOpen,
  onOpenChange,
  toggleOnly,
  ...props
}) => {
  const [stateOpen, setIsOpen] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : stateOpen;

  const toggleChat = () => {
    const newValue = !isOpen;
    setIsOpen(newValue);
    onOpenChange?.(newValue);
  };

  return (
    <div {...props}>
      {!toggleOnly && (
        <div
          ref={chatRef}
          className={cn(
            "flex flex-col bg-background border md:rounded-lg shadow-md overflow-hidden transition-all duration-250 ease-out  md:w-full md:h-full sm:z-[99999] fixed inset-0 md:inset-auto md:bottom-[10px] md:left-[80px]",
            chatConfig.dimensions[size],
            isOpen ? chatConfig.states.open : chatConfig.states.closed
          )}
          style={{
            zIndex: 999999
          }}
        >
          {children}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 sm:hidden"
            onClick={toggleChat}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <ExpandableChatToggle
        icon={icon}
        isOpen={isOpen}
        toggleChat={toggleChat}
        className={className}
      />
    </div>
  );
};

ExpandableChat.displayName = "ExpandableChat";

const ExpandableChatHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("flex items-center justify-between p-4 border-b", className)}
    ref={ref}
    {...props}
  />
));

ExpandableChatHeader.displayName = "ExpandableChatHeader";

const ExpandableChatBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("flex-grow overflow-y-auto", className)} {...props} />;

ExpandableChatBody.displayName = "ExpandableChatBody";

const ExpandableChatFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("border-t-0 p-4", className)} {...props} />;

ExpandableChatFooter.displayName = "ExpandableChatFooter";

interface ExpandableChatToggleProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  isOpen: boolean;
  toggleChat: () => void;
}

const ExpandableChatToggle: React.FC<ExpandableChatToggleProps> = ({
  className,
  icon,
  isOpen,
  toggleChat,
  ...props
}) => (
  <Button
    variant="default"
    onClick={toggleChat}
    className={cn(
      "w-14 h-14 rounded-full shadow-md flex items-center justify-center hover:shadow-lg hover:shadow-black/30 transition-all duration-300",
      className
    )}
    {...props}
  >
    {isOpen ? (
      <X className="h-6 w-6" />
    ) : (
      icon || <MessageCircle className="h-6 w-6" />
    )}
  </Button>
);

ExpandableChatToggle.displayName = "ExpandableChatToggle";

export {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter
};
